import { Button, Card, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import { useState } from 'react';
import ProductTrendsExportModal from '../Modals/ProductTrendsExportModal';
import CarouselCard from './CarouselCard';
import CarouselCardForCategories from './CarouselCardForCategories';

const ProductTrendsCards = ({ trends, startDate, endDate }) => {
  const noResults = <Text c="dimmed">There are no results for this product trend currently.</Text>;

  const [modalOpen, setModalOpen] = useState(false);
  const [exportTrend, setExportTrend] = useState();

  return (
    <>
      {modalOpen && (
        <ProductTrendsExportModal
          isOpen={modalOpen}
          close={() => setModalOpen(false)}
          scheduled={() => {
            setModalOpen(false);
            notifications.show({
              title: 'Export Scheduled',
              message:
                "Your export has been scheduled for processing. An email containing the export will be sent to you once it's ready.",
              color: 'success',
            });
          }}
          startDate={startDate}
          endDate={endDate}
          trend={exportTrend}
        />
      )}
      <Grid>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Stack gap={4}>
                <Title order={5} c="primary">
                  View-Boosting Categories
                </Title>
                <Text c="dimmed" size="sm">
                  Discover the categories that have attracted the most product views, indicating their trending status
                  among users.
                </Text>
              </Stack>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.productViewsByCategory?.length > 0 ? (
                <CarouselCardForCategories
                  hits={trends.productViewsByCategory}
                  metrics={[{ title: 'Product Views' }, { title: 'Products' }]}
                />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Stack gap={4}>
                <Title order={5} c="primary">
                  Top-Choice Categories
                </Title>
                <Text c="dimmed" size="sm">
                  Explore the categories with the highest number of purchases, showcasing the most preferred choices
                  among customers.
                </Text>
              </Stack>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.purchasesByCategory?.length > 0 ? (
                <CarouselCardForCategories
                  hits={trends.purchasesByCategory}
                  metrics={[{ title: 'Purchases' }, { title: 'Products' }]}
                />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Revenue Champions
                  </Title>
                  <Text c="dimmed" size="sm">
                    Discover the products that have generated the most revenue, reflecting their popularity and success
                    in driving sales.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'revenueForPeriod', title: 'Revenue Champions' });
                  }}
                  disabled={!trends?.revenueForPeriod?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.revenueForPeriod?.hits.length > 0 ? (
                <CarouselCard hits={trends.revenueForPeriod.hits} metrics={[{ title: 'Revenue' }]} />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Customer Favourites
                  </Title>
                  <Text c="dimmed" size="sm">
                    Explore the best-selling products that have won the hearts of our clients, indicating their trust
                    and satisfaction.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'purchasesForPeriod', title: 'Customer Favourites' });
                  }}
                  disabled={!trends?.purchasesForPeriod?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.purchasesForPeriod?.hits.length > 0 ? (
                <CarouselCard hits={trends.purchasesForPeriod.hits} metrics={[{ title: 'Purchases' }]} />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Discounted Winners
                  </Title>
                  <Text c="dimmed" size="sm">
                    Explore products that have seen success thanks to discounts, reflecting their popularity among
                    purchasers.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'purchasesOnDiscount', title: 'Discounted Winners' });
                  }}
                  disabled={!trends?.purchasesOnDiscount?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.purchasesOnDiscount?.hits.length > 0 ? (
                <CarouselCard hits={trends.purchasesOnDiscount.hits} metrics={[{ title: 'Purchases' }]} />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Window Shopper's Standouts
                  </Title>
                  <Text c="dimmed" size="sm">
                    Discover products that have garnered significant views without being added to carts, showcasing
                    their attraction to users.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'productViewsNoCartAdds', title: "Window Shopper's" });
                  }}
                  disabled={!trends?.productViewsNoCartAdds?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.productViewsNoCartAdds?.hits.length > 0 ? (
                <CarouselCard
                  hits={trends.productViewsNoCartAdds.hits}
                  metrics={[{ title: 'Total Views' }, { title: 'Cart Adds', count: 0 }]}
                />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Unpopular Picks
                  </Title>
                  <Text c="dimmed" size="sm">
                    Discover products that haven't been purchased yet but have garnered attention through views.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'productViewsNoCheckouts', title: 'Unpopular Picks' });
                  }}
                  disabled={!trends?.productViewsNoCheckouts?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.productViewsNoCheckouts?.hits.length > 0 ? (
                <CarouselCard
                  hits={trends.productViewsNoCheckouts.hits}
                  metrics={[{ title: 'Total Views' }, { title: 'Purchases', count: 0 }]}
                />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Abandoned Gems
                  </Title>
                  <Text c="dimmed" size="sm">
                    See products that shoppers have shown interest in by adding them to the cart but haven't gone on to
                    purchase.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'cartAddsNoCheckouts', title: 'Abandoned Gems' });
                  }}
                  disabled={!trends?.cartAddsNoCheckouts?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.cartAddsNoCheckouts?.hits.length > 0 ? (
                <CarouselCard
                  hits={trends.cartAddsNoCheckouts.hits}
                  metrics={[{ title: 'Cart Adds' }, { title: 'Purchases', count: 0 }]}
                />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Missed Opportunities
                  </Title>
                  <Text c="dimmed" size="sm">
                    Explore highly viewed products that were unfortunately out of stock at the time, indicating
                    potential unmet demand.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'productViewsOutOfStock', title: 'Missed Opportunities' });
                  }}
                  disabled={!trends?.productViewsOutOfStock?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.productViewsOutOfStock?.hits.length > 0 ? (
                <CarouselCard hits={trends.productViewsOutOfStock.hits} metrics={[{ title: 'Total Views' }]} />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Search-Driven Stars
                  </Title>
                  <Text c="dimmed" size="sm">
                    Explore products that have gained visibility thanks to referrals from search engines, such as
                    Google.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'searchEngineReferrer', title: 'Search-Driven Stars' });
                  }}
                  disabled={!trends?.searchEngineReferrer?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.searchEngineReferrer?.hits.length > 0 ? (
                <CarouselCard hits={trends.searchEngineReferrer.hits} metrics={[{ title: 'Search Engine Clicks' }]} />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={12}>
          <Card mb="md" withBorder>
            <Card.Section inheritPadding py="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={5} c="primary">
                    Social Buzz Collection
                  </Title>
                  <Text c="dimmed" size="sm">
                    Check out products that are generating buzz on social media platforms, like Facebook referrals,
                    drawing attention and views.
                  </Text>
                </Stack>
                <Button
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    setExportTrend({ trend: 'socialMediaReferrer', title: 'Social Buzz Collection' });
                  }}
                  disabled={!trends?.socialMediaReferrer?.hits.length}
                >
                  <IconDownload size={16} /> Export
                </Button>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="sm">
              {trends?.socialMediaReferrer?.hits.length > 0 ? (
                <CarouselCard hits={trends.socialMediaReferrer.hits} metrics={[{ title: 'Social Media Clicks' }]} />
              ) : (
                noResults
              )}
            </Card.Section>
          </Card>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default ProductTrendsCards;
